import type { UseQueryResult } from "@tanstack/react-query";

import { DemoContext } from "@/providers/demo/DemoContext";

type QueryConfig<T> = {
  query: (...args: any[]) => UseQueryResult<T>;
  params?: any[]; // For queries that take multiple params
  options?: any; // React Query options (will be merged with enabled flag)
};

/**
 * Utility hook that switches between regular and demo queries based on demo mode.
 *
 * Pass the query hooks and their options, and this hook will call only the appropriate one.
 * Automatically manages `enabled` flags based on demo mode and demoUserId availability.
 *
 * @param realConfig - Configuration for the regular query (disabled in demo mode)
 * @param demoConfig - Configuration for the demo query (disabled in regular mode)
 * @returns The appropriate query result based on current mode
 *
 * @example
 * ```tsx
 * // Simple query:
 * const { data } = useDemoAlternativeQuery(
 *   { query: ProfileAnalyticsQueries.useGetGradeScore, options: { refetchInterval: 10000 } },
 *   { query: ProfileDemoQueries.useGetAnalyticsGrade }
 * );
 *
 * // Query with params:
 * const { data } = useDemoAlternativeQuery(
 *   { query: ProfileAnalyticsQueries.useGetActionPrioritiesV2, params: [{}], options: { refetchInterval: 10000 } },
 *   { query: ProfileDemoQueries.useGetAnalyticsActionPriorities }
 * );
 * ```
 */
export const useDemoAlternativeQuery = <T>(
  realConfig: QueryConfig<T>,
  demoConfig: QueryConfig<T>,
): UseQueryResult<T> => {
  const { isDemoMode, demoUserId } = DemoContext.useDemo();

  // Prepare options with enabled flags
  // In demo mode, also check that demoUserId is available before enabling
  const realOptions = { ...realConfig.options, enabled: !isDemoMode && (realConfig.options?.enabled ?? true) };
  const demoOptions = {
    ...demoConfig.options,
    enabled: isDemoMode && !!demoUserId && (demoConfig.options?.enabled ?? true),
  };

  // Call both hooks but only enable the appropriate one (React hooks rules)
  const realResult = realConfig.params
    ? realConfig.query(...realConfig.params, realOptions)
    : realConfig.query(realOptions);

  const demoResult = demoConfig.params
    ? demoConfig.query(...demoConfig.params, demoOptions)
    : demoConfig.query(demoOptions);

  // Return the appropriate result based on mode
  return isDemoMode ? demoResult : realResult;
};
